"use client";

import { useState, ReactNode } from "react";
import { cn } from "@drpcrd/utils/shared";
import { Typography } from "./typography";

type ToggleGroupOption = {
  value: string;
  label: string;
  icon?: ReactNode;
};

type Props = {
  options: ToggleGroupOption[];
  value?: string;
  onValueChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
};

/**
 * A segmented control component for selecting between multiple mutually exclusive options.
 * Commonly used for link-in-bio details and other binary or multi-choice selections.
 */
export const ToggleGroup = ({
  options,
  value: controlledValue,
  onValueChange,
  disabled = false,
  className,
}: Props) => {
  const [uncontrolledValue, setUncontrolledValue] = useState(
    options[0]?.value ?? "",
  );

  const isControlled = controlledValue !== undefined;
  const currentValue = isControlled ? controlledValue : uncontrolledValue;

  const onSelect = (value: string) => {
    if (disabled) {
      return;
    }

    if (!isControlled) {
      setUncontrolledValue(value);
    }
    onValueChange(value);
  };

  return (
    <div
      className={cn(
        "flex w-full items-center gap-0 overflow-hidden rounded-lg bg-neutral-900",
        disabled && "pointer-events-none opacity-50",
        className,
      )}
      role="radiogroup"
    >
      {options.map((option) => {
        const isSelected = currentValue === option.value;

        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={isSelected}
            onClick={() => onSelect(option.value)}
            className={cn(
              "flex flex-1 items-center justify-center gap-1 p-1 transition-all duration-200",
              isSelected ? "bg-neutral-900 text-white" : "bg-white text-black",
              !disabled && "cursor-pointer",
            )}
          >
            {option.icon && (
              <span className="flex items-center justify-center [&>svg]:h-3 [&>svg]:w-3">
                {option.icon}
              </span>
            )}
            <Typography
              size="sm"
              weight="bold"
              className="whitespace-nowrap"
              color={isSelected ? "white" : "black"}
            >
              {option.label}
            </Typography>
          </button>
        );
      })}
    </div>
  );
};
